import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Async thunk for adding a blood test
export const addBloodTest = createAsyncThunk(
  "bloodTests/addBloodTest",
  async (
    { categoryId, testName, minAge, maxAge, gender },
    { rejectWithValue },
  ) => {
    if (!testName.trim()) {
      return rejectWithValue("Enter the Pathology Test name.");
    }

    // Validate age
    if ((minAge && !maxAge) || (!minAge && maxAge)) {
      return rejectWithValue(
        "Test - Min and Max age should be specified together as integers.",
      );
    }

    let parsedMin = null;
    let parsedMax = null;
    if (minAge && maxAge) {
      if (isNaN(minAge) || isNaN(maxAge)) {
        return rejectWithValue("Test - Min and Max age should be integers.");
      }
      parsedMin = parseInt(minAge);
      parsedMax = parseInt(maxAge);
      if (parsedMin >= parsedMax) {
        return rejectWithValue("Test - Min age should be less than Max age.");
      }
    }

    // Normalize gender
    const normalizedGender = gender === "Both" ? null : gender;

    try {
      const res = await axios.post("/api/bloodtests/", {
        category: categoryId,
        name: testName,
        min_age: parsedMin,
        max_age: parsedMax,
        gender: normalizedGender,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        `Test - ${testName} failed. ${err.response?.data || err.message}`,
      );
    }
  },
);

export const updateRangeValues = createAsyncThunk(
  "bloodTests/updateRangeValues",
  async (bloodTests, { rejectWithValue }) => {
    for (let testId in bloodTests) {
      const { testName, minVal, maxVal, prevType, daysVal, dminVal, dmaxVal } =
        bloodTests[testId];

      // Validate min/max
      if (minVal && isNaN(minVal)) {
        return rejectWithValue(`Test - ${testName}. Min Val is not a Float.`);
      }
      if (maxVal && isNaN(maxVal)) {
        return rejectWithValue(`Test - ${testName}. Max Val is not a Float.`);
      }
      if (minVal && maxVal && parseFloat(minVal) >= parseFloat(maxVal)) {
        return rejectWithValue(
          `Test - ${testName}. Max Val should be greater than Min Val.`,
        );
      }

      // Validate previous check logic
      if (prevType === "No Check") {
        if (daysVal || dminVal || dmaxVal) {
          return rejectWithValue(
            `Test - ${testName}. Extra values should be empty for No Check.`,
          );
        }
      }

      if (
        prevType === "Previous test result within X number of days" ||
        prevType === "Previous result"
      ) {
        if (
          prevType === "Previous test result within X number of days" &&
          (!daysVal || isNaN(daysVal))
        ) {
          return rejectWithValue(
            `Test - ${testName}. Days should be an integer.`,
          );
        }
        if (!dminVal && !dmaxVal) {
          return rejectWithValue(
            `Test - ${testName}. % below or % above should be specified.`,
          );
        }
        if (dminVal && isNaN(dminVal)) {
          return rejectWithValue(
            `Test - ${testName}. % below should be a float.`,
          );
        }
        if (dmaxVal && isNaN(dmaxVal)) {
          return rejectWithValue(
            `Test - ${testName}. % above should be a float.`,
          );
        }
      }
    }

    try {
      await axios.put("/api/bloodtests/update_ranges/", { bloodTests });
      return "Range values updated.";
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

const bloodTestsSlice = createSlice({
  name: "bloodTests",
  initialState: {
    items: [],
    message: null,
    messageColor: null,
    loading: false,
    categoryRule: 0,
  },
  reducers: {
    setCategoryRule: (state, action) => {
      state.categoryRule = action.payload;
    },
    clearMessage: (state) => {
      state.message = null;
      state.messageColor = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addBloodTest.pending, (state) => {
        state.loading = true;
        state.message = null;
      })
      .addCase(addBloodTest.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.message = `Test - ${action.payload.name} added.`;
        state.messageColor = "green";
      })
      .addCase(addBloodTest.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.messageColor = "red";
      })
      .addCase(updateRangeValues.pending, (state) => {
        state.loading = true;
        state.message = null;
      })
      .addCase(updateRangeValues.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.messageColor = "green";
      })
      .addCase(updateRangeValues.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.messageColor = "red";
      });
  },
});

export const { setCategoryRule, clearMessage } = bloodTestsSlice.actions;
export default bloodTestsSlice.reducer;
